import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiUsers, FiMessageSquare, FiActivity, FiTrendingUp
} from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import { Line, Bar } from 'react-chartjs-2';
import ResponsiveChart from '../../../components/ResponsiveChart';
import { transformCategoryToChart } from '../../../api/adapters/analyticsAdapter';
import type { LabeledSeries } from '../../../types/api';
import { useAIDispatch, useAISelector } from '../store';
import { fetchAIDashboardStats, fetchAIConversationTrends, fetchAIChannelPerformance } from '../store/slices/aiAnalyticsSlice';
import { fetchAIAssistants } from '../store/slices/aiAssistantSlice';
import { useAIModule } from '../contexts/AIModuleContext';

const AIDashboard: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const dispatch = useAIDispatch();
  const { theme } = useAIModule();
  
  const { dashboardStats, conversationTrends, channelPerformance, loading } = useAISelector((state: any) => state.aiAnalytics as {
    dashboardStats: { activeAssistants: number; totalConversations: number; messagesToday: number; successRate: number };
    conversationTrends: LabeledSeries;
    channelPerformance: LabeledSeries;
    loading: boolean;
  });
  const { assistants } = useAISelector(state => state.aiAssistants);

  useEffect(() => {
    dispatch(fetchAIDashboardStats());
    dispatch(fetchAIConversationTrends(selectedPeriod));
    dispatch(fetchAIChannelPerformance());
    dispatch(fetchAIAssistants());
  }, [dispatch, selectedPeriod]);

  const statCards = [
    {
      title: 'Active AI Assistants',
      value: dashboardStats.activeAssistants,
      change: 20,
      icon: <BiBot size={24} />,
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'AI Conversations',
      value: dashboardStats.totalConversations?.toLocaleString() || '0',
      change: 15,
      icon: <FiMessageSquare size={24} />,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'AI Messages Today',
      value: dashboardStats.messagesToday?.toLocaleString() || '0',
      change: -5,
      icon: <FiActivity size={24} />,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'AI Success Rate',
      value: `${dashboardStats.successRate || 0}%`,
      change: 3,
      icon: <FiTrendingUp size={24} />,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const lineChartData = {
    labels: conversationTrends.labels || [],
    datasets: [
      {
        label: 'AI Conversations',
        data: conversationTrends.data || [],
        fill: true,
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        tension: 0.4,
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
          Looplet AI Dashboard
        </h1>
        <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Monitor your AI assistants performance in real-time
        </p>
        
        {/* Period Selector */}
        <div className="flex gap-2 mt-4">
          {['24h', '7d', '30d', '90d'].map((period) => (
            <motion.button
              key={period}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-lg transition-all ${
                selectedPeriod === period
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : theme === 'dark' 
                    ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {period}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            className={`relative overflow-hidden rounded-2xl p-6 backdrop-blur-lg ${
              theme === 'dark' 
                ? 'bg-gray-800/80 border border-gray-700/50' 
                : 'bg-white/90 border border-gray-200/50 shadow-lg'
            }`}
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${stat.color}`}>
                  {stat.icon}
                </div>
                <motion.div
                  animate={{ rotate: stat.change > 0 ? 0 : 180 }}
                  className={`text-sm font-semibold ${
                    stat.change > 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {stat.change > 0 ? '↑' : '↓'} {Math.abs(stat.change)}%
                </motion.div>
              </div>
              <h3 className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {stat.title}
              </h3>
              <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {stat.value}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Chart */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        className={`rounded-2xl p-6 ${
          theme === 'dark' 
            ? 'bg-gray-800/80 border border-gray-700/50' 
            : 'bg-white/90 border border-gray-200/50 shadow-lg'
        }`}
      >
        <h3 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          AI Message Activity
        </h3>
        <div style={{ height: 300 }}>
          <ResponsiveChart ariaLabel="AI message activity over time">
            <Line 
              data={lineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    labels: { color: theme === 'dark' ? '#94A3B8' : '#64748B' }
                  }
                },
                scales: {
                  x: {
                    grid: { color: theme === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.1)' },
                    ticks: { color: theme === 'dark' ? '#94A3B8' : '#64748B' }
                  },
                  y: {
                    grid: { color: theme === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.1)' },
                    ticks: { color: theme === 'dark' ? '#94A3B8' : '#64748B' }
                  }
                }
              }}
            />
          </ResponsiveChart>
        </div>
      </motion.div>

      {/* Channel Distribution */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className={`mt-8 rounded-2xl p-6 ${
          theme === 'dark' 
            ? 'bg-gray-800/80 border border-gray-700/50' 
            : 'bg-white/90 border border-gray-200/50 shadow-lg'
        }`}
      >
        <h3 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          AI Channel Distribution
        </h3>
        <div style={{ height: 300 }}>
          <ResponsiveChart ariaLabel="AI channel distribution">
            <Bar
              data={transformCategoryToChart(channelPerformance, 'Channel Usage') as any}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: {
                    grid: { color: theme === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.1)' },
                    ticks: { color: theme === 'dark' ? '#94A3B8' : '#64748B' }
                  },
                  y: {
                    grid: { color: theme === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.1)' },
                    ticks: { color: theme === 'dark' ? '#94A3B8' : '#64748B' }
                  }
                }
              }}
            />
          </ResponsiveChart>
        </div>
      </motion.div>

      {/* Active AI Assistants */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className={`mt-8 rounded-2xl p-6 ${
          theme === 'dark' 
            ? 'bg-gray-800/80 border border-gray-700/50' 
            : 'bg-white/90 border border-gray-200/50 shadow-lg'
        }`}
      >
        <h3 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Active AI Assistants
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assistants.length > 0 ? assistants.slice(0, 6).map((assistant: any, index: number) => (
            <motion.div
              key={assistant.id || index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className={`p-4 rounded-xl cursor-pointer ${
                theme === 'dark' 
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
                  : 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${
                    index % 3 === 0 ? 'from-purple-500 to-pink-500' :
                    index % 3 === 1 ? 'from-blue-500 to-cyan-500' :
                    'from-green-500 to-emerald-500'
                  }`}>
                    <BiBot size={20} />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {assistant.name || `AI Assistant ${index + 1}`}
                    </h4>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Active now
                    </p>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </div>
              <div className={`flex justify-between text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <span>{Math.floor(Math.random() * 100) + 20} msgs/hr</span>
                <span>{Math.floor(Math.random() * 30) + 70}% success</span>
              </div>
            </motion.div>
          )) : (
            <div className={`col-span-full text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No AI assistants created yet. Create your first AI assistant to get started!
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AIDashboard;